// facility-ecommerce/src/hooks/useFocusTrap.js
import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing focus trap in modals/drawers
 */
export const useFocusTrap = (isActive, returnFocusTo = null) => {
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Store the currently focused element
    previousActiveElement.current = document.activeElement;

    const container = containerRef.current;
    if (!container) return;

    // Get all focusable elements
    const getFocusableElements = () => {
      const focusableSelectors = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]:not([disabled])'
      ].join(', ');

      return container.querySelectorAll(focusableSelectors);
    };

    // Focus the first element
    const focusFirstElement = () => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    };

    // Handle tab navigation
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Add event listener
    container.addEventListener('keydown', handleKeyDown);

    // Focus first element after a brief delay to ensure rendering
    const focusTimeout = setTimeout(focusFirstElement, 100);

    // Cleanup
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      clearTimeout(focusTimeout);
    };
  }, [isActive]);

  // Restore focus when deactivated
  useEffect(() => {
    if (!isActive && previousActiveElement.current) {
      // Return focus to the specified element or the previously focused element
      const targetElement = returnFocusTo || previousActiveElement.current;
      if (targetElement && typeof targetElement.focus === 'function') {
        targetElement.focus();
      }
      previousActiveElement.current = null;
    }
  }, [isActive, returnFocusTo]);

  return containerRef;
};

/**
 * Custom hook for managing body scroll lock and click blocking
 */
export const useScrollLock = (isLocked) => {
  useEffect(() => {
    if (!isLocked) return;

    // Store original styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPaddingRight = originalStyle.paddingRight;

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Apply scroll lock and add classes for drawer targeting (dual classes for backward compatibility)
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${parseInt(originalPaddingRight) + scrollbarWidth}px`;
    document.body.classList.add('no-scroll', 'fc-open');

    // Cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.body.classList.remove('no-scroll', 'fc-open');
    };
  }, [isLocked]);
};